import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import * as React from "react";
import { LazyIcon } from "../../components/LazyIcon";
import { useOptionalRampState } from "../../containers/RampStateContainer";
import { Preference, Stack as StackType, Tenant } from "../../types";
import { AircraftPreferencesListItemMenuButton } from "./AircraftPreferencesListItemMenu";
import { AircraftSpecsTooltip } from "./AircraftSpecsTooltip";
import { NoteTooltip } from "./NoteTooltip";

const PlacementSetting = ({ icon, text }) => (
  <Stack
    key={`PlacementSetting-${text.replace(/ /g, "-")}`}
    direction="row"
    spacing={1}
    alignItems="center"
  >
    {icon}
    <Typography variant="caption">{text}</Typography>
  </Stack>
);

const ORDERED_PREFERENCES = [
  Preference.TAIL_IN,
  Preference.NOSE_IN,
  Preference.PREFER_FRONT_ROW,
  Preference.PREFER_BACK_ROW,
  Preference.OK_TO_LEAVE_OUT,
  Preference.LOCKED,
  Preference.TAIL_WHEEL_NOSE_IN,
  Preference.TAIL_WHEEL_TAIL_IN,
];

type AircraftListItemPresenterProps = {
  stack: StackType;
  setStack: (stack: StackType) => void;
  tenant: Tenant;
  setTenant: (tenant: Tenant) => void;
  onSelectAircraft: () => void;
  onDeleteAircraft: () => void;
  showBottomBorder?: boolean;
  onIncrementalPark?: (aircraftId: string) => void;
};

export const AircraftListItemPresenter: React.FC<
  AircraftListItemPresenterProps
> = ({
  stack,
  setStack,
  tenant,
  setTenant,
  onSelectAircraft,
  onDeleteAircraft,
  onIncrementalPark,
}) => {
  const rampState = useOptionalRampState();
  const isRamp = Boolean(rampState?.ramp);
  const theme = useTheme();
  return (
    <Stack
      className={tenant.selected && "selected"}
      data-element="li_current_aircraft"
      spacing={1}
      sx={{
        backgroundColor: tenant.selected && `${theme.palette.success.main}15`,
        p: 0,
        py: 1.5, // Vertical padding only
        cursor: "pointer",
      }}
      onClick={onSelectAircraft}
    >
      <Stack direction="row" justifyContent="space-between" sx={{ px: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {Boolean(tenant.tail_number) && (
            <Chip size="small" label={tenant.tail_number} />
          )}
          {tenant.type === "base" ? (
            <Chip size="small" variant="outlined" label="Tenant" />
          ) : (
            <Chip size="small" variant="outlined" label="Transient" />
          )}
        </Stack>
        <Box onClick={(evt) => evt.stopPropagation()}>
          <AircraftPreferencesListItemMenuButton
            tenant={tenant}
            stack={stack}
            setStack={setStack}
            setTenant={setTenant}
            preferences={tenant.position.preferences}
            setPreferences={(preferences) => {
              setTenant({
                ...tenant,
                position: {
                  ...tenant.position,
                  preferences,
                },
              });
            }}
            onDeleteAircraft={onDeleteAircraft}
            onIncrementalPark={onIncrementalPark}
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5 }}>
        <Typography>{tenant.aircraft.model}</Typography>
        <AircraftSpecsTooltip aircraft={tenant.aircraft} />
        <NoteTooltip note={tenant.note} />
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ px: 1.5 }}
        justifyContent="start"
        spacing={2}
      >
        {isRamp
          ? []
          : ORDERED_PREFERENCES.filter(
              (p) => tenant.position.preferences.indexOf(p) > -1
            ).map((setting) => {
              let content = null;

              if (setting === Preference.NOSE_IN) {
                content = (
                  <PlacementSetting
                    icon={<LazyIcon name="FlightIcon" fontSize="small" />}
                    text="Nose In"
                  />
                );
              }
              if (setting === Preference.TAIL_IN) {
                content = (
                  <PlacementSetting
                    icon={
                      <LazyIcon
                        name="FlightIcon"
                        fontSize="small"
                        sx={{ transform: "rotate(180deg)" }}
                      />
                    }
                    text="Tail In"
                  />
                );
              }
              if (setting === Preference.TAIL_WHEEL_NOSE_IN) {
                content = (
                  <PlacementSetting
                    icon={<LazyIcon name="FlightIcon" fontSize="small" />}
                    text="Nose In"
                  />
                );
              }

              if (setting === Preference.TAIL_WHEEL_TAIL_IN) {
                content = (
                  <PlacementSetting
                    icon={
                      <LazyIcon
                        name="FlightIcon"
                        fontSize="small"
                        sx={{ transform: "rotate(180deg)" }}
                      />
                    }
                    text="Tail In"
                  />
                );
              }

              if (setting === Preference.PREFER_FRONT_ROW) {
                content = (
                  <PlacementSetting
                    icon={
                      <LazyIcon
                        name="SwitchLeftIcon"
                        fontSize="small"
                        sx={{ transform: "rotate(90deg)" }}
                      />
                    }
                    text="Front"
                  />
                );
              }
              if (setting === Preference.PREFER_BACK_ROW) {
                content = (
                  <PlacementSetting
                    icon={
                      <LazyIcon
                        name="SwitchLeftIcon"
                        fontSize="small"
                        sx={{ transform: "rotate(-90deg)" }}
                      />
                    }
                    text="Back"
                  />
                );
              }
              if (setting === Preference.OK_TO_LEAVE_OUT) {
                content = (
                  <PlacementSetting
                    icon={<LazyIcon name="BeachAccessIcon" fontSize="small" />}
                    text="OK Outside"
                  />
                );
              }
              if (setting === Preference.LOCKED) {
                content = (
                  <PlacementSetting
                    icon={<LazyIcon name="LockIcon" fontSize="small" />}
                    text="Locked"
                  />
                );
              }
              return (
                <React.Fragment key={`setting-${setting}`}>
                  {content}
                </React.Fragment>
              );
            })}
      </Stack>
    </Stack>
  );
};

type Props = {
  tenant: Tenant;
  stack: StackType;
  setStack: (stack: StackType) => void;
  showBottomBorder?: boolean;
  setTenant: (t: Tenant) => void;
  onSelectAircraft: (t: Tenant) => void;
  onDeleteAircraft: (t: Tenant) => void;
  onIncrementalPark?: (aircraftId: string) => void;
};

export const AircraftListItem: React.FC<Props> = ({
  stack,
  setStack,
  tenant,
  showBottomBorder,
  setTenant,
  onSelectAircraft,
  onDeleteAircraft,
  onIncrementalPark,
}) => {
  return (
    <AircraftListItemPresenter
      tenant={tenant}
      stack={stack}
      setStack={setStack}
      setTenant={setTenant}
      onSelectAircraft={() => onSelectAircraft(tenant)}
      onDeleteAircraft={() => onDeleteAircraft(tenant)}
      showBottomBorder={showBottomBorder}
      onIncrementalPark={onIncrementalPark}
    />
  );
};
